import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import {
  ArqueoPayment,
  ArqueoTotals,
  formatYmd,
} from "@/lib/arqueo/aggregations";

export interface DailyClosure {
  id: string;
  gym_id: string;
  closure_date: string;
  closed_at: string;
  closed_by: string;
  total_amount: number;
  totals_by_method: Record<string, number>;
  totals_by_category: Record<string, number>;
  articles_breakdown: any[];
  payment_ids: string[];
  notes: string | null;
}

export const useDailyClosure = (gymId: string | null, date: Date) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useNotifications();
  const ymd = formatYmd(date);

  const query = useQuery({
    queryKey: ["arqueo-closure", gymId, ymd],
    queryFn: async (): Promise<DailyClosure | null> => {
      if (!gymId) return null;
      const { data, error } = await supabase
        .from("daily_cash_closures")
        .select("*")
        .eq("gym_id", gymId)
        .eq("closure_date", ymd)
        .maybeSingle();
      if (error) throw error;
      return (data as DailyClosure) || null;
    },
    enabled: !!gymId,
  });

  const closeDayMutation = useMutation({
    mutationFn: async ({
      payments,
      totals,
      notes,
    }: {
      payments: ArqueoPayment[];
      totals: ArqueoTotals;
      notes?: string;
    }) => {
      if (!gymId || !user) throw new Error("Falta gimnasio o usuario");
      const payload = {
        gym_id: gymId,
        closure_date: ymd,
        closed_by: user.id,
        total_amount: totals.total,
        totals_by_method: totals.byMethod,
        totals_by_category: totals.byCategory,
        articles_breakdown: totals.articlesBreakdown,
        payment_ids: payments.map((p) => p.id),
        notes: notes || null,
      };
      const { data, error } = await supabase
        .from("daily_cash_closures")
        .insert(payload as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["arqueo-closure", gymId, ymd] });
      success("Día cerrado", { description: "El arqueo quedó registrado." });
    },
    onError: (e: any) => {
      console.error(e);
      showError("No se pudo cerrar el día", { description: e.message });
    },
  });

  return {
    closure: query.data,
    isLoading: query.isLoading,
    closeDay: closeDayMutation,
  };
};
